import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { EmployeeImage } from '@/components/EmployeeImage';
import { Loader2, Users } from 'lucide-react';
import type { ProjectDetails } from '@/types/project';

export const TeamTab: React.FC<{ project: ProjectDetails; isLoadingTeamMembers: boolean }> = ({ project, isLoadingTeamMembers }) => {
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Project Manager</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            <Avatar className="h-12 w-12">
              <AvatarFallback>
                {project.projectManager.split(' ').map(n => n[0]).join('')}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-medium">{project.projectManager}</p>
              <p className="text-sm text-muted-foreground">{project.projectManagerEmail}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            Team Members ({project.teamMembers.length})
            {isLoadingTeamMembers && (
              <Loader2 className="h-4 w-4 ml-2 inline animate-spin" />
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoadingTeamMembers ? (
            <div className="flex items-center justify-center h-[400px]">
              <div className="text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                <p className="text-sm text-muted-foreground mt-2">Loading team members...</p>
              </div>
            </div>
          ) : project.teamMembers.length === 0 ? (
            <div className="flex items-center justify-center h-[400px]">
              <div className="text-center">
                <Users className="h-8 w-8 mx-auto text-muted-foreground" />
                <p className="text-sm text-muted-foreground mt-2">No team members assigned</p>
              </div>
            </div>
          ) : (
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-4">
                {project.teamMembers.map((member) => (
                  <div key={member.id} className="flex items-start justify-between border-b pb-4 last:border-0">
                    <div className="flex items-center gap-3">
                      <EmployeeImage 
                        name={member.name}
                        photoUrl={member.photoUrl}
                        size="md"
                      />
                      <div>
                        <p className="font-medium text-sm">{member.name}</p>
                        <p className="text-xs text-muted-foreground">{member.role}</p>
                        <p className="text-xs text-muted-foreground">{member.email}</p>
                      </div>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {member.allocation}% allocated
                    </Badge>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </>
  );
};
